import {
  ArrowLeftOutlined,
  BarsOutlined,
  EditOutlined,
  ExportOutlined,
  FileProtectOutlined,
  LineChartOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  RedoOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  AProgress,
  ATable,
  DropdownOptions,
  PageIntro,
  PagePanel,
} from '../../components';
import { useToast } from '../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { ITreatment } from '../../shared/interfaces';
import {
  MenuProps,
  Button,
  Input,
  Dropdown,
  Segmented,
  Tooltip,
  Tag,
} from 'antd';
import { currency } from '../../shared/utils/functions';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApprovalContainer,
  PendentContainer,
  TreatmentsFilterCard,
  TreatmentsLayout,
  TreatmentsStatItem,
  TreatmentsStatsCard,
  TreatmentsStatsGrid,
  TreatmentsSummaryGrid,
  TreatmentsToolbar,
} from './styles';
import { getTreatments } from '../../http';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { useI18n } from '../../shared/i18n';

const Treatments: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t } = useI18n();
  const [search, setSearch] = useState<string>('');
  const [treatments, setTreatments] = useState<ITreatment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [type, setType] = useState<number | null>(null);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const totalTreatments = treatments.length;
  const approved = treatments.filter((t) =>
    t.approval !== null && t.approval !== undefined ? Boolean(t.approval) : false,
  ).length;
  const pending = treatments.filter(
    (t) => t.approval === null || t.approval === undefined,
  ).length;
  const inProgress = treatments.filter((t) => Number(t.progress) > 0).length;

  const renderEllipsis = (value: string) => (
    <Tooltip title={value}>
      <span
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </span>
    </Tooltip>
  );

  const listTreatments = useCallback(async () => {
    setLoading(true);
    setTreatments([]);

    try {
      const response = await getTreatments(
        client_id,
        limit,
        (page - 1) * limit,
        search,
        type,
      );
      setPages(response.count);
      setTreatments(response.data);
    } catch {
      addToast({
        type: 'error',
        title: t('treatmentsPage.toast.errorTitle'),
        description: t('treatmentsPage.toast.listError'),
      });
    } finally {
      setLoading(false);
    }
  }, [client_id, limit, search, page, type, addToast, t]);

  const menuItems: MenuProps['items'] = [
    {
      label: t('treatmentsPage.actions.refresh'),
      key: '1',
      icon: <RedoOutlined />,
      onClick: listTreatments,
    },
    ...(!client_id
      ? permissionFind([PermissionActions.treatmentsCreate])
        ? [
            {
              label: t('treatmentsPage.actions.create'),
              key: '2',
              icon: <PlusCircleOutlined />,
              onClick: () => navigate(`/treatment/new`),
            },
          ]
        : []
      : []),
    {
      label: t('treatmentsPage.actions.reports'),
      key: '3',
      icon: <PrinterOutlined />,
      children: [
        {
          label: t('treatmentsPage.actions.production'),
          key: '3-1',
          onClick: () => navigate('/treatments/reports/production'),
        },
        {
          label: t('treatmentsPage.actions.treatments'),
          key: '3-2',
          onClick: () => navigate('/treatments/reports/treatments'),
        },
        {
          label: t('treatmentsPage.actions.procedures'),
          key: '3-3',
          onClick: () => navigate('/treatments/reports/procedures'),
        },
      ],
    },
  ];

  useEffect(() => {
    listTreatments();
  }, [page, search, limit, listTreatments]);

  return (
    <TreatmentsLayout>
      <PageIntro
        badge={t('treatmentsPage.intro.badge')}
        icon={<FileProtectOutlined />}
        title={t('treatmentsPage.intro.title')}
        description={t('treatmentsPage.intro.description')}
        actions={
          !client_id ? (
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button icon={<BarsOutlined />} type="primary">
                {t('treatmentsPage.actions.moreActions')}
              </Button>
            </Dropdown>
          ) : undefined
        }
      />

      <PagePanel>
        <TreatmentsToolbar>
          <div style={{ flex: 1, minWidth: 260 }}>
            {client_id ? (
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                {t('treatmentsPage.actions.back')}
              </Button>
            ) : (
              <Input
                placeholder={t('treatmentsPage.searchPlaceholder')}
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={listTreatments}
                allowClear
              />
            )}
          </div>
        </TreatmentsToolbar>

        <TreatmentsSummaryGrid>
          <TreatmentsStatsCard>
            <TreatmentsStatsGrid>
              <TreatmentsStatItem>
                <span className="label">{t('treatmentsPage.stats.total')}</span>
                <span className="value">{totalTreatments}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">
                  {t('treatmentsPage.stats.approved')}
                </span>
                <span className="value success">{approved}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">
                  {t('treatmentsPage.stats.pending')}
                </span>
                <span className="value warning">{pending}</span>
              </TreatmentsStatItem>
              <TreatmentsStatItem>
                <span className="label">
                  {t('treatmentsPage.stats.inProgress')}
                </span>
                <span className="value info">{inProgress}</span>
              </TreatmentsStatItem>
            </TreatmentsStatsGrid>
          </TreatmentsStatsCard>

          <TreatmentsFilterCard>
            <Segmented
              value={type === null ? 'all' : String(type)}
              onChange={(val) => {
                setPage(1);
                setType(val === 'all' ? null : Number(val));
              }}
              options={[
                { label: t('treatmentsPage.filters.all'), value: 'all' },
                { label: t('treatmentsPage.filters.budgets'), value: '0' },
                { label: t('treatmentsPage.filters.treatments'), value: '1' },
              ]}
            />
          </TreatmentsFilterCard>
        </TreatmentsSummaryGrid>
      </PagePanel>

      <PagePanel>
        <ATable
          scroll={{ x: 1100, y: 510 }}
          columns={[
            {
              title: t('treatmentsPage.table.id'),
              width: 80,
              dataIndex: 'code',
            },
            {
              title: t('treatmentsPage.table.type'),
              width: 70,
              dataIndex: 'type',
              render: (value: number) => (
                <>
                  {value > 0
                    ? t('treatmentsPage.table.typeTreatment')
                    : t('treatmentsPage.table.typeBudget')}
                </>
              ),
            },
            {
              title: t('treatmentsPage.table.client'),
              dataIndex: 'client_name',
              width: 240,
              render: (value: string) => renderEllipsis(value),
            },
            {
              title: t('treatmentsPage.table.professional'),
              dataIndex: 'professional_name',
              width: 220,
              render: (value: string) => renderEllipsis(value),
            },
            {
              title: t('treatmentsPage.table.total'),
              width: 120,
              render: (cell: ITreatment) => (
                <>{currency(cell.total - cell.discount)}</>
              ),
            },
            {
              title: t('treatmentsPage.table.progress'),
              dataIndex: 'progress',
              width: 180,
              render: (value: number) => <AProgress percent={value} />,
            },
            {
              title: t('treatmentsPage.table.situation'),
              dataIndex: 'approval',
              width: 120,
              render: (value: boolean | number | null | undefined) => {
                const coerced =
                  value === null || value === undefined
                    ? null
                    : Boolean(value);
                return (
                  <>
                    {coerced !== null ? (
                      <ApprovalContainer success={coerced}>
                        {coerced
                          ? t('treatmentsPage.table.approved')
                          : t('treatmentsPage.table.rejected')}
                      </ApprovalContainer>
                    ) : (
                      <PendentContainer>
                        {t('treatmentsPage.table.pending')}
                      </PendentContainer>
                    )}
                  </>
                );
              },
            },
            {
              title: t('treatmentsPage.table.signature'),
              width: 120,
              render: (cell: ITreatment) => {
                const isSigned = Boolean(
                  cell.signature_validation_code || cell.signed_at,
                );

                return (
                  <Tag color={isSigned ? 'success' : 'warning'}>
                    {isSigned
                      ? t('treatmentsPage.table.signed')
                      : t('treatmentsPage.table.unsigned')}
                  </Tag>
                );
              },
            },
            {
              title: t('treatmentsPage.table.options'),
              width: 130,
              fixed: 'right' as const,
              render: (cell: ITreatment) => {
                return (
                  <DropdownOptions
                    items={[
                      permissionFind([PermissionActions.treatmentsUpdate])
                        ? {
                            label: cell.approval
                              ? t('treatmentsPage.actions.view')
                              : t('treatmentsPage.actions.approveReject'),
                            key: '1',
                            icon: <ExportOutlined />,
                            onClick: () =>
                              navigate(`/treatment/${cell.id}/approval`),
                          }
                        : null,
                      permissionFind([PermissionActions.treatmentsUpdate])
                        ? {
                            label: t('treatmentsPage.actions.edit'),
                            key: '2',
                            icon: <EditOutlined />,
                            onClick: () => navigate(`/treatment/${cell.id}`),
                            disabled: cell.approval,
                          }
                        : null,
                      cell.type === 1 &&
                      cell.approval &&
                      permissionFind([PermissionActions.treatmentsUpdate])
                        ? {
                            label: t('treatmentsPage.actions.evolution'),
                            key: '3',
                            icon: <LineChartOutlined />,
                            onClick: () =>
                              navigate(`/treatment/${cell.id}/evolution`),
                          }
                        : null,
                      {
                        label: t('treatmentsPage.actions.print'),
                        key: '4',
                        icon: <PrinterOutlined />,
                        onClick: () => navigate(`/treatment/${cell.id}/print`),
                      },
                    ]}
                  />
                );
              },
            },
          ]}
          loading={loading}
          dataSource={treatments}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </TreatmentsLayout>
  );
};

export default Treatments;
